import { Component, DestroyRef, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Authentification } from '../../Services/authentification';
import { TicketCardItem } from '../../Services/EntiteProjet';
import { CategorieSynthese, SyntheseConnexionApi } from '../../Services/synthese-connexion-api';
import { formaterCompteurSynthese } from './compteur-synthese';

export type ChoixSynthese = { ticket: TicketCardItem } | { liste: true };
type Rubrique = {
  id: CategorieSynthese;
  titre: string;
  aide: string;
  icone: string;
  urgent: boolean;
};
export const RUBRIQUES_SYNTHESE: readonly Rubrique[] = [
  {
    id: 'nouveaux',
    titre: 'Nouveaux dysfonctionnements',
    aide: 'Voir dans menu ticket',
    icone: 'engineering',
    urgent: true,
  },
  {
    id: 'retards',
    titre: 'Interventions planifiées en retard',
    aide: 'À transformer en bon d’intervention, modifier ou supprimer',
    icone: 'event_busy',
    urgent: true,
  },
  {
    id: 'messages',
    titre: 'Nouveaux messages',
    aide: 'sur des tickets existants',
    icone: 'chat_bubble',
    urgent: false,
  },
  {
    id: 'bons',
    titre: 'Nouveaux bons d’intervention',
    aide: 'postés depuis votre dernière connexion',
    icone: 'description',
    urgent: false,
  },
];
type Liste = {
  tickets: TicketCardItem[];
  total: number | null;
};
const listeVide = (): Liste => ({ tickets: [], total: null });

@Component({
  selector: 'app-synthese-connexion',
  imports: [MatDialogModule],
  templateUrl: './synthese-connexion.html',
  styleUrl: './synthese-connexion.css',
})
export class SyntheseConnexion {
  private readonly api = inject(SyntheseConnexionApi);
  private readonly auth = inject(Authentification);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject<MatDialogRef<SyntheseConnexion, ChoixSynthese>>(MatDialogRef);
  readonly prenom = inject<{ prenom: string }>(MAT_DIALOG_DATA).prenom;
  readonly rubriques = RUBRIQUES_SYNTHESE;
  readonly formaterCompteur = formaterCompteurSynthese;
  private generation = 0;
  readonly chargement = signal(true);
  readonly erreur = signal('');
  readonly ouverte = signal<CategorieSynthese | null>(null);
  readonly listes = signal<Record<string, Liste>>(
    Object.fromEntries(this.rubriques.map((r) => [r.id, listeVide()])),
  );
  private readonly session = this.auth.sessionVersion;

  constructor() {
    void this.charger();
  }

  private valide(generation: number): boolean {
    return (
      !this.destroyRef.destroyed &&
      this.session === this.auth.sessionVersion &&
      generation === this.generation
    );
  }

  async charger(): Promise<void> {
    const generation = ++this.generation;
    this.ouverte.set(null);
    this.chargement.set(true);
    this.erreur.set('');
    try {
      const resume = await this.api.charger();
      if (!this.valide(generation)) return;
      for (const rubrique of this.rubriques) {
        const categorie = resume[rubrique.id];
        const tickets: TicketCardItem[] = categorie.tickets.map((ticket) => ({
          id: String(ticket.id),
          installationId: ticket.installation?.id,
          site: ticket.installation?.nom ?? '',
          reference: ticket.numero,
          issue: ticket.libelle,
          dateDebut: '',
          vu: false,
        }));
        this.listes.update((listes) => ({
          ...listes,
          [rubrique.id]: { total: categorie.total, tickets },
        }));
      }
      this.ouverte.set(null);
    } catch {
      if (this.valide(generation)) this.erreur.set('La synthèse est indisponible.');
    } finally {
      if (this.valide(generation)) this.chargement.set(false);
    }
  }

  basculer(id: CategorieSynthese): void {
    this.ouverte.update((v) => (v === id ? null : id));
  }

  ouvrirTicket(ticket: TicketCardItem): void {
    if (ticket.id) this.dialog.close({ ticket });
  }

  voirTickets(): void {
    this.dialog.close({ liste: true });
  }
}
